/**
 * Copyright 2021-2022 Highway9 Networks Inc.
 */
import { createSlice, createAsyncThunk, createEntityAdapter, createSelector, PayloadAction, type EntityState } from "@reduxjs/toolkit";
import { RootState } from "..";
import { mobilityProfileAttributeServices, mobilityService } from "../../services/mobility-service";
import { MobilityProfile } from "../../types/mobilityProfile";
import { MobilityProfileAttributes } from "~/types/mobilityProfileAttributes";
import { useState } from "react";
import { useAppSelector, useAppDispatch } from "../hooks";
import { createSocketPatchReducer } from "../events/socketPatch";

export const mobilityProfilesAdapter = createEntityAdapter<MobilityProfile>({
  selectId: (profile) => profile.id!,
});
export const mobilityProfileAttributesAdapter = createEntityAdapter<MobilityProfileAttributes>();

interface MobilityProfileState {
  open: boolean;
  selectedId: string | null;
  profilesData: EntityState<MobilityProfile>;
  attributesData: EntityState<MobilityProfileAttributes>;
}

const initialState: MobilityProfileState = {
  open: false,
  selectedId: null,
  profilesData: mobilityProfilesAdapter.getInitialState(),
  attributesData: mobilityProfileAttributesAdapter.getInitialState(),
};

export const useMobilityProfileAttributes = () => {
  const attributesData = useAppSelector(mobilityProfileAttributesData);

  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);

  async function getAttributesData() {
    try {
      await dispatch(fetchMobilityProfileAttributes());
      setIsLoading(false);
    } catch (error) {
      console.log(error);
    }
  }

  return {
    attributesData,
    isLoading,
    setIsLoading,
    getAttributesData,
    dispatch,
  };
};

export const useMobilityProfiles = () => {
  const MobilityData = useAppSelector(mobilityProfileData);
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);

  async function getMobilityProfilesData() {
    try {
      await dispatch(fetchMobilityProfiles());
      setIsLoading(false);
    } catch (error) {
      console.log(error);
    }
  }

  return {
    MobilityData,
    isLoading,
    setIsLoading,
    getMobilityProfilesData,
    dispatch,
  };
};

export const fetchMobilityProfiles = createAsyncThunk("mobilityProfile/fetchMobilityProfiles", async () => {
  const data = await mobilityService.getMobilityProfiles();
  return data;
});

export const fetchMobilityProfileAttributes = createAsyncThunk("attribute/fetchMobilityProfileAttributes", async () => {
  const data = await mobilityProfileAttributeServices.getMobilityProfilesAttributes();

  return data;
});

const mobilityProfileSlice = createSlice({
  name: "mobilityProfile",
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.open = action.payload;
    },
    setSelected: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    applySocketPatches: createSocketPatchReducer(mobilityProfilesAdapter, (s) => s.profilesData),
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMobilityProfiles.fulfilled, (state, action) => {
      mobilityProfilesAdapter.setAll(state.profilesData, action.payload);
    });
    builder.addCase(fetchMobilityProfileAttributes.fulfilled, (state, action) => {
      mobilityProfileAttributesAdapter.setAll(state.attributesData, action.payload);
    });
  },
});

export const mobilityProfileActions = mobilityProfileSlice.actions;
export default mobilityProfileSlice.reducer;

const mobilityProfileEntitySelectors = mobilityProfilesAdapter.getSelectors(
  (state: RootState) => state.mobilityProfile.profilesData,
);
const mobilityProfileAttributeEntitySelectors = mobilityProfileAttributesAdapter.getSelectors(
  (state: RootState) => state.mobilityProfile.attributesData,
);

export const mobilityProfileData = mobilityProfileEntitySelectors.selectAll;
export const mobilityProfileEntities = mobilityProfileEntitySelectors.selectEntities;

export const mobilityProfileState = createSelector(
  [(state: RootState) => state.mobilityProfile.selectedId, mobilityProfileEntities],
  (selectedId, entities): MobilityProfile | null => (selectedId ? entities[selectedId] ?? null : null),
);

export const mobilityProfileOpen = (state: RootState) => state.mobilityProfile.open;
export const mobilityProfileAttributesData = mobilityProfileAttributeEntitySelectors.selectAll;
export const mobilityProfileAttributeEntities = mobilityProfileAttributeEntitySelectors.selectEntities;
